import { FCElement } from '@fc/component';
import { sectionManager } from '@fc/sections';
import { findHTMLNode, importHTMLNodes } from '@fc/section-response';
import { requestIdleCallback, viewTransition } from '@fc/utilities';
import { FCEvents } from '@fc/events';
import { PaginatedListAspectRatioHelper } from '@fc/pagination-ratio';

export default class PaginatedList extends FCElement {
  pages = new Map();
  infinityScrollObserver = null;

  #fuo = null;
  #fut = null;
  #ac = null;
  #resolveNext = null;
  #resolvePrev = null;
  #arHelper;
  #fetchGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#ac = new AbortController();
    this.#fetchGeneration += 1;

    const templateCard = this.querySelector('[data-ref="cardGallery"]');
    if (templateCard) {
      this.#arHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    document.addEventListener(FCEvents.filterChange, this.#handleFilterUpdate, {
      signal: this.#ac.signal,
      passive: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = null;

    this.#fuo?.disconnect();
    this.#fuo = null;

    if (this.#fut !== null) {
      clearTimeout(this.#fut);
      this.#fut = null;
    }

    this.#ac?.abort();
    this.#ac = null;

    this.#resolveNext?.();
    this.#resolvePrev?.();
    this.#resolveNext = null;
    this.#resolvePrev = null;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    this.infinityScrollObserver ??= new IntersectionObserver(
      async (entries) => {
        await viewTransition?.current;

        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const { viewMorePrevious, viewMoreNext } = this.refs;
          if (entry.target === viewMorePrevious) this.#renderPage('previous');
          else if (entry.target === viewMoreNext) this.#renderPage('next');
        }
      },
      { rootMargin: '100px' },
    );

    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    const { page } = pageInfo;
    if (!Number.isInteger(page) || page < 1) return false;
    const lastPage = Number(this.refs.grid?.dataset.lastPage);
    return lastPage >= page;
  }

  #getPage(type) {
    const { cards } = this.refs;
    if (!cards?.length) return undefined;

    const isPrev = type === 'previous';
    const targetCard = cards[isPrev ? 0 : cards.length - 1];
    const currentCardPage = Number(targetCard?.dataset.page);
    if (!currentCardPage || currentCardPage < 1) return undefined;

    const page = isPrev ? currentCardPage - 1 : currentCardPage + 1;
    if (page < 1) return undefined;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return { page, url };
  }

  #getGridForPage(page) {
    const doc = this.pages.get(page);
    if (!(doc instanceof Document)) return undefined;

    const grid = findHTMLNode(doc, '[data-ref="grid"]');
    if (!(grid instanceof Element)) return undefined;

    return importHTMLNodes(doc, ':scope > [data-ref="cards[]"]', { scope: grid });
  }

  async #fetchPage(type) {
    const generation = this.#fetchGeneration;
    const page = this.#getPage(type);
    const resolve = () => {
      if (type === 'next') {
        this.#resolveNext?.();
        this.#resolveNext = null;
      } else {
        this.#resolvePrev?.();
        this.#resolvePrev = null;
      }
    };

    if (!this.#shouldUsePage(page)) {
      resolve();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url, generation);
    resolve();
  }

  async #fetchSpecificPage(pageNumber, url, generation = this.#fetchGeneration) {
    if (!Number.isInteger(pageNumber) || pageNumber < 1) return;

    url ??= (() => {
      const u = new URL(window.location.href);
      u.searchParams.set('page', String(pageNumber));
      u.hash = '';
      return u;
    })();

    if (!this.#shouldUsePage({ page: pageNumber })) return;

    const content = await sectionManager.getSectionDocument(this.sectionId, {
      cache: true,
      signal: this.lifecycleSignal,
      url,
    });

    if (!this.isConnected || generation !== this.#fetchGeneration) return;
    if (content) this.pages.set(pageNumber, content);
  }

  // Shared render logic for both directions
  async #renderPage(type) {
    const { grid } = this.refs;
    if (!grid) return;

    const isPrev = type === 'previous';
    const pageInfo = this.#getPage(type);
    if (!this.#shouldUsePage(pageInfo)) return;

    let items = this.#getGridForPage(pageInfo.page);

    if (!items) {
      const promise = new Promise((r) => {
        if (isPrev) this.#resolvePrev = r;
        else this.#resolveNext = r;
      });
      this.#fetchPage(type);
      await promise;
      items = this.#getGridForPage(pageInfo.page);
      if (!items) return;
    }

    // Capture scroll state before prepend
    let scrollTop, firstEl, oldTop;
    if (isPrev) {
      scrollTop = window.scrollY;
      firstEl = grid.firstElementChild;
      oldTop = firstEl ? firstEl.getBoundingClientRect().top + scrollTop : 0;
    }

    if (isPrev) grid.prepend(...items);
    else grid.append(...items);

    this.#arHelper?.processNewElements();

    if (pageInfo.url) {
      history.pushState('', '', pageInfo.url.toString());
    }

    // Restore scroll position after prepend
    if (isPrev && firstEl) {
      const newTop = firstEl.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: scrollTop + (newTop - oldTop), behavior: 'instant' });
    }

    requestIdleCallback(() => this.#fetchPage(type));
  }

  #handleFilterUpdate = () => {
    this.#fetchGeneration += 1;
    this.pages.clear();

    this.#resolveNext?.();
    this.#resolvePrev?.();
    this.#resolveNext = null;
    this.#resolvePrev = null;

    this.#fuo?.disconnect();
    this.#fuo = null;

    if (this.#fut !== null) {
      clearTimeout(this.#fut);
      this.#fut = null;
    }

    const currentLastPage = this.refs.grid?.dataset.lastPage;

    this.#fuo = new MutationObserver(() => {
      const newLastPage = this.refs.grid?.dataset.lastPage;
      if (newLastPage !== currentLastPage) {
        this.#fuo?.disconnect();
        this.#fuo = null;
        if (!this.isConnected) return;
        this.#observeViewMore();
        this.#fetchPage('next');
      }
    });

    const { grid } = this.refs;
    if (grid) {
      this.#fuo.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      // Stop watching after 3s if nothing changes
      this.#fut = setTimeout(() => {
        this.#fuo?.disconnect();
        this.#fuo = null;
      }, 3000);
    }
  };
}
